import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Avatar,
  IconButton,
  Paper,
  Grid,
  TextField,
  InputAdornment,
  Chip,
  alpha,
  Fade,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Divider,
  Snackbar,
  Alert,
  Tabs,
  Tab
} from '@mui/material';
import {
  FilterList as FilterListIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  Bookmark as BookmarkIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  GetApp as GetAppIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { doc, updateDoc, arrayUnion, arrayRemove, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { ExportContent } from './ExportDialog';
import { v4 as uuidv4 } from 'uuid';

// Common styles for scrollbars
const commonStyles = {
  scrollbar: {
    '&::-webkit-scrollbar': {
      width: '8px',
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: (theme) => alpha(theme.palette.primary.main, 0.2),
      borderRadius: '20px',
      border: '2px solid transparent',
      backgroundClip: 'padding-box',
      transition: 'background 0.2s ease',
      '&:hover': {
        background: (theme) => alpha(theme.palette.primary.main, 0.3),
      }
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
      border: 'none',
    },
    scrollbarWidth: 'thin',
    scrollbarColor: (theme) => `${alpha(theme.palette.primary.main, 0.2)} transparent`,
    '&:hover': {
      '&::-webkit-scrollbar-thumb': {
        background: (theme) => alpha(theme.palette.primary.main, 0.3),
      }
    }
  },
};

// Helper function to normalize strings for search
const getNormalizedString = (str) => {
  if (!str) return '';
  return String(str).toLowerCase()
    .replace(/[åäáàâ]/g, 'a')
    .replace(/[öóòôø]/g, 'o')
    .replace(/[éèêë]/g, 'e')
    .replace(/[üúùû]/g, 'u')
    .replace(/[íìîï]/g, 'i');
};

const AdvancedFiltersDialog = ({ 
  open, 
  onClose, 
  categories = [],
  filters = {},
  onFilterChange,
  setFilters, 
  setFilteredHotels,
  hotels = [],
  createFilterOptions,
  onExportClick
}) => {
  const { user } = useAuth();
  const [customSearchValue, setCustomSearchValue] = useState('');
  const [savedFilterCombos, setSavedFilterCombos] = useState([]);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [newComboName, setNewComboName] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCombo, setSelectedCombo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [dialogTab, setDialogTab] = useState(0); // 0: Filter, 1: Export
  
  // Ref to track if a save operation is in progress
  const saveInProgressRef = useRef(false);

  // Load saved filter combinations from Firebase
  const loadSavedFilterCombos = useCallback(async () => {
    if (!user) return;
    
    try {
      setIsLoading(true);
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists() && userDoc.data().filterCombos) {
        setSavedFilterCombos(userDoc.data().filterCombos);
      } else {
        setSavedFilterCombos([]);
      }
    } catch (error) {
      console.error('Error loading filter combinations:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load saved filters',
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  // Reset custom search when dialog closes
  useEffect(() => {
    if (!open) {
      setCustomSearchValue('');
    }
  }, [open]);

  // Load filter combos when dialog opens and user is logged in
  useEffect(() => {
    if (open && user) {
      loadSavedFilterCombos();
    }
  }, [open, user, loadSavedFilterCombos]);

  // Handle custom search input change
  const handleCustomSearchChange = (e) => {
    setCustomSearchValue(e.target.value);
  };

  // Apply custom search
  const applyCustomSearch = () => {
    if (!customSearchValue.trim()) return;
    
    const searchText = customSearchValue.trim();
    
    // Get current text search filters and add the new one
    const currentTextSearches = filters.textSearch || [];
    
    // Only add if it's not already there (prevent duplicates)
    if (!currentTextSearches.includes(searchText)) {
      const newTextSearches = [...currentTextSearches, searchText];
      
      // Use the parent component's filter handler directly but don't update local state
      // This ensures the parent component handles all filter logic consistently
      onFilterChange('textSearch', newTextSearches);
    }
    
    // Reset the input field
    setCustomSearchValue('');
  };

  // Clear custom search
  const clearCustomSearch = () => {
    setCustomSearchValue('');
    
    // Use the parent component's filter handler, don't update local state
    onFilterChange('textSearch', []);
  };

  // Remove a single text search term
  const removeTextSearch = (termToRemove) => {
    if (!filters.textSearch) return;
    
    const newTextSearches = filters.textSearch.filter(term => term !== termToRemove);
    
    // Use the parent component's filter handler, don't update local state
    onFilterChange('textSearch', newTextSearches);
  };

  // Handle export action from ExportContent
  const handleExport = async (exportData) => {
    try {
      setIsExporting(true);
      
      if (exportData.format === 'csv') {
        // Create and download CSV file
        const blob = new Blob([exportData.content], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', exportData.filename);
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        // For Excel format, we need to dynamically load the xlsx library
        try {
          // Use dynamic import to load the xlsx library only when needed
          const XLSX = await import('https://cdn.jsdelivr.net/npm/xlsx@0.18.5/+esm');
          
          // Create workbook
          const wb = XLSX.utils.book_new();
          
          // Create worksheet data - array of objects with proper key-value pairs
          const wsData = [];
          const { headers, rows } = exportData.content;
          
          // Convert rows to an array of objects with header keys
          for (let i = 0; i < rows.length; i++) {
            const rowObj = {};
            for (let j = 0; j < headers.length; j++) {
              // Use header as the key, row data as the value
              rowObj[headers[j]] = rows[i][j];
            }
            wsData.push(rowObj);
          }
          
          // Create worksheet using json_to_sheet for proper column structure
          const ws = XLSX.utils.json_to_sheet(wsData);
          
          // Set column widths for better readability
          const colWidths = headers.map(header => ({ wch: Math.max(header.length * 1.5, 10) }));
          ws['!cols'] = colWidths;
          
          // Add worksheet to workbook
          XLSX.utils.book_append_sheet(wb, ws, "Hotels");
          
          // Generate Excel file and trigger download
          XLSX.writeFile(wb, exportData.filename);
        } catch (error) {
          console.error("Error loading XLSX library or creating Excel file:", error);
          // Fallback to CSV if Excel export fails
          alert("Excel export failed. Please try CSV format instead or check console for details.");
          
          // Create and download CSV file as fallback
          const blob = new Blob([convertToCSV(exportData.headers, exportData.rows)], { type: 'text/csv;charset=utf-8;' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.setAttribute('href', url);
          link.setAttribute('download', exportData.filename.replace('.xlsx', '.csv'));
          
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      }
      
          setSnackbar({
            open: true,
        message: `Successfully exported ${exportData.count} hotels`,
            severity: 'success'
          });
    } catch (error) {
      console.error('Export error:', error);
      setSnackbar({
        open: true,
        message: `Export failed: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setIsExporting(false);
    }
  };

  // Helper function to convert data to CSV (for fallback)
  const convertToCSV = (headers, rows) => {
    // Create header row
    const headerRow = headers.map(header => `"${header}"`).join(',');
    
    // Create data rows
    const dataRows = rows.map(row => {
      return row.map(value => {
        // Escape any double quotes and wrap in quotes to handle commas
        return `"${value?.toString().replace(/"/g, '""') || ''}"`;
      }).join(',');
    }).join('\n');
    
    // Combine headers and data
    return `${headerRow}\n${dataRows}`;
  };

  // Memoize the filtered hotels for the export tab
  const filteredHotels = React.useMemo(() => {
    // If we're filtering, we need to generate the filtered list
    if (Object.keys(filters).length > 0) {
      return hotels.filter(hotel => {
        // Check if the hotel matches all the filters
        return Object.entries(filters).every(([field, values]) => {
          if (!values || values.length === 0) return true;
          
          // Handle text search filter differently
          if (field === 'textSearch') {
            // Return true if the hotel matches ALL of the search terms
            return values.every(searchText => {
              const searchLower = getNormalizedString(searchText);
              
              // Check in category data
              if (hotel.categoryData) {
                for (const key in hotel.categoryData) {
                  const value = hotel.categoryData[key]?.value;
                  if (value && getNormalizedString(value).includes(searchLower)) {
                    return true;
                  }
                }
              }
              
              // Check direct hotel properties
              if (
                (hotel.name && getNormalizedString(hotel.name).includes(searchLower)) ||
                (hotel.notes && getNormalizedString(hotel.notes).includes(searchLower)) ||
                (hotel.id && getNormalizedString(hotel.id).includes(searchLower))
              ) {
                return true;
              }
              
              // Check tags
              if (Array.isArray(hotel.tags)) {
                for (const tag of hotel.tags) {
                  if (tag && getNormalizedString(tag).includes(searchLower)) {
                    return true;
                  }
                }
              }
              
              return false;
            });
          }
          
          // Regular category filters
          const categoryKey = `category_${field.toLowerCase()}`;
          const fieldValue = hotel.categoryData?.[categoryKey]?.value || '';
          
          // Handle checkbox fields
          const fieldItem = categories.flatMap(group => group.items).find(item => item.fieldName === field);
          if (fieldItem?.type === 'checkbox') {
            // For checkbox, check if value is true (checked)
            // Handle various representations of true/false
            let isChecked = false;
            if (fieldValue === true) {
              isChecked = true;
            } else if (fieldValue !== false && fieldValue !== null && fieldValue !== undefined && fieldValue !== '') {
              const strValue = String(fieldValue).toLowerCase().trim();
              isChecked = strValue === 'true' || strValue === 'yes' || strValue === '1' || strValue === 'checked';
            }
            // Filter value should be 'true' or true
            return values.some(value => (value === 'true' || value === true) && isChecked);
          }
          
          // Field must match at least one value in the filter array
          return values.some(value => fieldValue === value);
        });
      });
    }
    
    // If no filters are applied, return all hotels
    return hotels;
  }, [filters, hotels, categories]);

  if (!categories || !Array.isArray(categories)) {
    return null;
  }

  // Use all categories without filtering by search term
  const filteredCategories = categories;

  const handleSaveComboClick = () => {
    if (Object.keys(filters).length === 0) {
          setSnackbar({
            open: true,
        message: 'No active filters to save',
        severity: 'warning'
      });
      return;
    }
    setSaveDialogOpen(true);
  };

  const saveFilterCombo = async () => {
    if (saveInProgressRef.current) return;
    if (!user) {
      setSnackbar({
        open: true,
        message: 'You must be logged in to save filters',
        severity: 'error'
      });
      return;
    }
    
    const comboName = newComboName.trim();
    if (!comboName) {
        setSnackbar({
          open: true,
        message: 'Please enter a name for your filter combination',
        severity: 'warning'
      });
      return;
    }
    
    if (Object.keys(filters).length === 0) {
      setSnackbar({
        open: true,
        message: 'No active filters to save',
        severity: 'warning'
      });
      return;
    }
    
    try {
      saveInProgressRef.current = true;
      setIsSaving(true);
      
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
      
      const filterCombo = {
        id: uuidv4(),
        name: comboName,
        filters: filters,
        createdAt: new Date().toISOString()
      };
      
      if (userDoc.exists()) {
        await updateDoc(userDocRef, {
          filterCombos: arrayUnion(filterCombo)
        });
      } else {
        await updateDoc(userDocRef, {
          filterCombos: [filterCombo]
        });
      }
      
      setSavedFilterCombos(prev => [...prev, filterCombo]);
      setNewComboName('');
      setSaveDialogOpen(false);
      
      setSnackbar({
        open: true,
        message: `Filter combination "${comboName}" saved successfully`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error saving filter combination:', error);
      setSnackbar({
        open: true,
        message: `Failed to save filter combination: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setIsSaving(false);
        saveInProgressRef.current = false;
    }
  };

  const loadFilterCombo = (combo) => {
    // Clear current filters first by calling onFilterChange with empty arrays
    Object.keys(filters).forEach(field => {
      onFilterChange(field, []);
    });
    
    // Apply each filter from the combo by calling onFilterChange
    Object.entries(combo.filters).forEach(([field, values]) => {
      if (values && values.length > 0) {
        onFilterChange(field, values);
      }
    });
    
    // Close the menu
      setAnchorEl(null);
    
    setSnackbar({
      open: true,
      message: `Filter combination "${combo.name}" loaded`,
      severity: 'success'
    });
  };

  const handleComboMenuClick = (event, combo) => {
    setAnchorEl(event.currentTarget);
    setSelectedCombo(combo);
  };

  const handleComboMenuClose = () => {
    setAnchorEl(null);
    setSelectedCombo(null);
  };

  const handleDeleteCombo = async () => {
    if (!user || !selectedCombo) return;
    
    try {
      const userDocRef = doc(db, 'users', user.uid);
      await updateDoc(userDocRef, {
        filterCombos: arrayRemove(selectedCombo)
      });
      
      setSavedFilterCombos(prev => prev.filter(c => c.id !== selectedCombo.id));
      setSnackbar({
        open: true,
        message: `Filter combination "${selectedCombo.name}" deleted`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error deleting filter combination:', error);
      setSnackbar({
        open: true,
        message: `Failed to delete filter combination: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setAnchorEl(null);
      setSelectedCombo(null);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 3 },
            minHeight: { xs: '100%', sm: 'auto' },
            maxHeight: { xs: '100%', sm: '85vh' },
            bgcolor: 'background.paper',
            backgroundImage: 'none',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: theme => `
              0 24px 48px ${alpha(theme.palette.common.black, 0.15)},
              0 8px 16px ${alpha(theme.palette.common.black, 0.1)}
            `,
            overflow: 'hidden',
            // Enhanced glassmorphism effect
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: theme => theme.palette.mode === 'dark' 
                ? 'linear-gradient(135deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0.05) 100%)'
                : 'linear-gradient(135deg, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0.4) 100%)',
              pointerEvents: 'none',
              zIndex: -1
            }
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: { xs: 3, sm: 4 },
          bgcolor: 'transparent',
          borderBottom: '1px solid',
          borderColor: 'divider',
          position: 'relative',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
            <Box sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                bgcolor: theme => alpha(theme.palette.primary.main, 0.12),
                transform: 'scale(1.02)',
              }
            }}>
              {dialogTab === 0 ? <FilterListIcon sx={{ fontSize: 24 }} /> : <GetAppIcon sx={{ fontSize: 24 }} />}
            </Box>
            <Box>
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 500,
                  mb: 0.5,
                  fontFamily: 'StrawberrySansDisplay',
                  letterSpacing: '-0.02em',
                  color: 'text.primary',
                  fontSize: { xs: '1.5rem', sm: '1.75rem' }
                }}
              >
                {dialogTab === 0 ? 'Filter Hotels' : 'Export Hotel Data'}
              </Typography>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ 
                  opacity: 0.8,
                  fontFamily: 'StrawberrySansText',
                  fontSize: '0.875rem'
                }}
              >
                {dialogTab === 0 
                  ? 'Filter hotels by various criteria' 
                  : (filteredHotels.length > 0 
                    ? `Exporting ${filteredHotels.length} filtered hotels` 
                    : `Exporting all ${hotels.length} hotels`)}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {user && dialogTab === 0 && (
              <Tooltip title="Save current filters">
                <IconButton 
                  onClick={handleSaveComboClick}
                  disabled={Object.keys(filters).length === 0}
                  sx={{ 
                    color: 'primary.main',
                    borderRadius: 2,
                    '&:hover': {
                      bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
                    },
                    '&.Mui-disabled': {
                      color: 'text.disabled'
                    }
                  }}
                >
                  <SaveIcon />
                </IconButton>
              </Tooltip>
            )}
            <IconButton 
              onClick={onClose}
              sx={{ 
                color: 'text.secondary',
                borderRadius: 2,
                '&:hover': {
                  color: 'error.main',
                  bgcolor: (theme) => alpha(theme.palette.error.light, 0.1)
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <Tabs
          value={dialogTab}
          onChange={(_, newValue) => setDialogTab(newValue)}
          sx={{ 
            px: { xs: 3, sm: 4 }, 
            bgcolor: 'transparent',
            borderBottom: '1px solid',
            borderColor: 'divider',
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px 3px 0 0',
              bgcolor: 'primary.main'
            }
          }}
        >
          <Tab 
            label="Filters" 
            sx={{ 
              fontFamily: 'StrawberrySansText', 
              textTransform: 'none', 
              fontWeight: 500,
              fontSize: '0.95rem',
              minHeight: 56,
              px: 3,
              borderRadius: '12px 12px 0 0',
              '&.Mui-selected': {
                color: 'primary.main',
                fontWeight: 600
              },
              '&:hover': {
                bgcolor: theme => alpha(theme.palette.primary.main, 0.04),
                borderRadius: '12px 12px 0 0'
              }
            }} 
          />
          <Tab 
            label="Export" 
            sx={{ 
              fontFamily: 'StrawberrySansText', 
              textTransform: 'none', 
              fontWeight: 500,
              fontSize: '0.95rem',
              minHeight: 56,
              px: 3,
              borderRadius: '12px 12px 0 0',
              '&.Mui-selected': {
                color: 'primary.main',
                fontWeight: 600
              },
              '&:hover': {
                bgcolor: theme => alpha(theme.palette.primary.main, 0.04),
                borderRadius: '12px 12px 0 0'
              }
            }} 
          />
        </Tabs>

        <DialogContent sx={{ 
          p: 0,
          position: 'relative',
          zIndex: 1,
          overflowX: 'hidden',
          bgcolor: 'transparent',
          ...commonStyles.scrollbar
        }}>
          {/* Filter Tab */}
          {dialogTab === 0 && (
          <Box sx={{ 
            p: { xs: 3, sm: 4 },
            background: 'transparent'
          }}>
              {/* Custom Search Section */}
              <Paper
                elevation={0}
                sx={{
                  mb: 4,
                  p: 4,
                  borderRadius: 4,
                  bgcolor: 'background.paper',
                  boxShadow: theme => `0 2px 8px ${alpha(theme.palette.common.black, 0.04)}`,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: theme => `0 8px 24px ${alpha(theme.palette.common.black, 0.12)}`
                  }
                }}
              >
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1.5,
                  mb: 3
                }}>
                  <Box sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1.5,
                    bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
                    color: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <SearchIcon sx={{ fontSize: 18 }} />
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily: 'StrawberrySansDisplay',
                      fontWeight: 500,
                      color: 'text.primary',
                      fontSize: '1.25rem'
                    }}
                  >
                    Custom Text Search
                  </Typography>
                  {filters.textSearch?.length > 0 && (
                    <Chip
                      label={filters.textSearch.length}
                      size="small"
                      color="primary"
                      sx={{ 
                        height: 24,
                        ml: 1,
                        '& .MuiChip-label': { px: 1, fontSize: '0.75rem' }
                      }}
                    />
                  )}
                </Box>
                
                {/* Display current search terms as chips */}
                {filters.textSearch?.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    {filters.textSearch.map((term, index) => (
                      <Chip
                        key={`${term}-${index}`}
                        label={term}
                        size="small"
                        color="primary"
                        onDelete={() => removeTextSearch(term)}
                        sx={{ 
                          borderRadius: 2,
                          height: 28,
                          '& .MuiChip-label': { px: 1.5, fontSize: '0.8rem', fontWeight: 500 },
                          '& .MuiChip-deleteIcon': {
                            width: 18,
                            height: 18,
                            '&:hover': {
                              color: 'primary.dark'
                            }
                          }
                        }}
                      />
                    ))}
                    {filters.textSearch?.length > 0 && (
                      <Chip
                        label="Clear All"
                        size="small"
                        color="default"
                        onClick={clearCustomSearch}
                        sx={{ 
                          borderRadius: 2,
                          height: 28,
                          bgcolor: 'error.light',
                          color: 'white',
                          '&:hover': {
                            bgcolor: 'error.main',
                            transform: 'scale(1.02)'
                          },
                          transition: 'all 0.2s ease',
                          '& .MuiChip-label': { px: 1.5, fontSize: '0.8rem', fontWeight: 500 }
                        }}
                      />
                    )}
                  </Box>
                )}
                
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <TextField
                    fullWidth
                    placeholder="Search for IP addresses, specific text in any field..."
                    value={customSearchValue}
                    onChange={handleCustomSearchChange}
                    size="small"
                    variant="outlined"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: customSearchValue && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => setCustomSearchValue('')}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: theme => alpha(theme.palette.background.paper, 0.8),
                        borderRadius: 2.5,
                        backdropFilter: 'blur(8px)',
                        WebkitBackdropFilter: 'blur(8px)',
                        border: '1px solid',
                        borderColor: 'divider',
                        '&:hover': {
                          bgcolor: theme => alpha(theme.palette.background.paper, 0.9),
                          borderColor: 'primary.light',
                          transform: 'translateY(-1px)',
                          boxShadow: theme => `0 4px 12px ${alpha(theme.palette.primary.main, 0.1)}`
                        },
                        '&.Mui-focused': {
                          bgcolor: 'background.paper',
                          borderColor: 'primary.main',
                          boxShadow: theme => `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}, 0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                        },
                        transition: 'all 0.2s ease-in-out',
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: 'none'
                        }
                      },
                      '& .MuiInputBase-input': {
                        fontFamily: 'StrawberrySansText',
                        fontSize: '0.95rem'
                      }
                    }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && customSearchValue.trim()) {
                        e.preventDefault();
                        applyCustomSearch();
                      }
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={applyCustomSearch}
                    disabled={!customSearchValue.trim()}
                    sx={{
                      minWidth: 'auto',
                      px: 3,
                      py: 1.5,
                      borderRadius: 2.5,
                      whiteSpace: 'nowrap',
                      fontFamily: 'StrawberrySansText',
                      fontWeight: 600,
                      textTransform: 'none',
                      boxShadow: theme => `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: theme => `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`
                      },
                      '&:disabled': {
                        bgcolor: 'action.disabledBackground',
                        color: 'action.disabled',
                        boxShadow: 'none'
                      },
                      transition: 'all 0.2s ease-in-out'
                    }}
                  >
                    Add
                  </Button>
                </Box>
              </Paper>

            {/* Saved Filter Combinations */}
            {user && (savedFilterCombos.length > 0 || isLoading) && (
              <Paper
                elevation={0}
                sx={{
                  mb: 4,
                  p: 4,
                  borderRadius: 4,
                  bgcolor: 'background.paper',
                  boxShadow: theme => `0 2px 8px ${alpha(theme.palette.common.black, 0.04)}`,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: theme => `0 8px 24px ${alpha(theme.palette.common.black, 0.12)}`
                  }
                }}
              >
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1.5,
                  mb: 3
                }}>
                  <Box sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1.5,
                    bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
                    color: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <BookmarkIcon sx={{ fontSize: 18 }} />
                  </Box>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 500,
                      fontFamily: 'StrawberrySansDisplay',
                      color: 'text.primary',
                      fontSize: '1.25rem'
                    }}
                  >
                    Saved Filter Combinations
                  </Typography>
                  {isLoading && (
                    <Box 
                      sx={{ 
                        width: 16, 
                        height: 16, 
                        borderRadius: '50%',
                        border: '2px solid',
                        borderColor: (theme) => alpha(theme.palette.primary.main, 0.2),
                        borderTopColor: 'primary.main',
                        animation: 'spin 1s linear infinite',
                        '@keyframes spin': {
                          '0%': {
                            transform: 'rotate(0deg)',
                          },
                          '100%': {
                            transform: 'rotate(360deg)',
                          },
                        },
                      }}
                    />
                  )}
                </Box>
                
                {isLoading && savedFilterCombos.length === 0 ? (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    height: 40,
                    color: 'text.secondary',
                    fontSize: '0.875rem'
                  }}>
                    Loading saved combinations...
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {savedFilterCombos.map(combo => {
                      // Count total filters in this combination
                      const totalFilters = Object.values(combo.filters).reduce(
                        (sum, values) => sum + values.length, 0
                      );
                      
                      return (
                        <Chip
                          key={combo.id}
                          icon={<BookmarkIcon fontSize="small" />}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography component="span" sx={{ fontSize: 'inherit', fontFamily: 'StrawberrySansText' }}>
                                {combo.name}
                              </Typography>
                              <Chip
                                label={totalFilters}
                                size="small"
                                color="primary"
                                sx={{ 
                                  height: 16, 
                                  '& .MuiChip-label': { 
                                    px: 0.75, 
                                    fontSize: '0.625rem',
                                    fontWeight: 'bold'
                                  }
                                }}
                              />
                            </Box>
                          }
                            onClick={() => loadFilterCombo(combo)}
                          onDelete={(e) => {
                            e.stopPropagation();
                              handleComboMenuClick(e, combo);
                          }}
                          deleteIcon={
                            <IconButton size="small">
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          }
                          sx={{
                            height: 32,
                            borderRadius: 2,
                            bgcolor: 'background.paper',
                            border: '1px solid',
                            borderColor: 'divider',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                            '& .MuiChip-label': {
                              px: 1,
                              display: 'flex',
                              alignItems: 'center',
                            },
                            '&:hover': {
                              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                              borderColor: 'primary.main',
                              boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                              '& .MuiChip-icon': {
                                color: 'primary.main'
                              }
                            },
                            '& .MuiChip-icon': {
                              color: (theme) => alpha(theme.palette.primary.main, 0.7)
                            }
                          }}
                        />
                      );
                    })}
                  </Box>
                )}
              </Paper>
            )}

            {/* Hotel Status Filter */}
            <Paper
              elevation={0}
              sx={{
                mb: 4,
                p: 4,
                borderRadius: 4,
                bgcolor: 'background.paper',
                boxShadow: theme => `0 2px 8px ${alpha(theme.palette.common.black, 0.04)}`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: theme => `0 8px 24px ${alpha(theme.palette.common.black, 0.12)}`
                }
              }}
            >
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1.5,
                mb: 3
              }}>
                <Box sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 1.5,
                  bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
                  color: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <FilterListIcon sx={{ fontSize: 18 }} />
                </Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: 'StrawberrySansDisplay',
                    fontWeight: 500,
                    color: 'text.primary',
                    fontSize: '1.25rem'
                  }}
                >
                  Hotel Status
                </Typography>
                {filters.status?.length > 0 && (
                  <Chip
                    label={filters.status.length}
                    size="small"
                    color="primary"
                    sx={{ 
                      height: 24,
                      ml: 1,
                      '& .MuiChip-label': { px: 1.5, fontSize: '0.8rem', fontWeight: 500 }
                    }}
                  />
                )}
                {filters.status?.length > 0 && (
                  <Button
                    size="small"
                    onClick={() => {
                      const newFilters = { ...filters };
                      delete newFilters.status;
                      setFilters(newFilters);
                      onFilterChange('status', []);
                    }}
                    sx={{ 
                      color: 'text.secondary',
                      '&:hover': { 
                        color: 'error.main'
                      }
                    }}
                  >
                    Clear
                  </Button>
                )}
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {[
                  { value: 'active', label: 'Active Hotels', color: '#52C41A' },
                  { value: 'work_in_progress', label: 'Work in Progress', color: '#1890FF' }
                ].map((option) => {
                  const isSelected = filters.status?.includes(option.value);
                  // Count hotels with this status
                  const count = hotels.filter(hotel => hotel.status === option.value).length;
                  const totalHotels = hotels.length;
                  const percentage = totalHotels > 0 ? Math.round((count / totalHotels) * 100) : 0;
                  
                  return (
                    <Paper
                      key={option.value}
                      elevation={0}
                      onClick={() => {
                        let newValues = [...(filters.status || [])];
                        if (isSelected) {
                          newValues = newValues.filter(v => v !== option.value);
                        } else {
                          newValues.push(option.value);
                        }
                        onFilterChange('status', newValues);
                      }}
                      sx={{
                        p: 3,
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: isSelected ? option.color : 'divider',
                        bgcolor: isSelected 
                          ? alpha(option.color, 0.06)
                          : 'background.paper',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        minWidth: 180,
                        flex: 1,
                        boxShadow: theme => `0 2px 8px ${alpha(theme.palette.common.black, 0.04)}`,
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          borderColor: option.color,
                          boxShadow: theme => `0 8px 24px ${alpha(theme.palette.common.black, 0.12)}`,
                          bgcolor: isSelected 
                            ? alpha(option.color, 0.08)
                            : alpha(option.color, 0.02),
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: option.color,
                            flexShrink: 0
                          }} />
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontFamily: 'StrawberrySansDisplay',
                              fontWeight: 500,
                              color: isSelected ? option.color : 'text.primary',
                              fontSize: '1rem'
                            }}
                          >
                            {option.label}
                          </Typography>
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'text.secondary',
                            fontSize: '0.75rem',
                            opacity: 0.7
                          }}
                        >
                          {percentage}%
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography
                          variant="h5"
                          sx={{
                            fontFamily: 'StrawberrySansDisplay',
                            fontWeight: 600,
                            color: option.color,
                            fontSize: '1.5rem',
                            lineHeight: 1
                          }}
                        >
                          {count}
                        </Typography>
                        
                        <Box sx={{
                          width: 40,
                          height: 4,
                          borderRadius: 2,
                          bgcolor: alpha(option.color, 0.15),
                          overflow: 'hidden',
                          position: 'relative'
                        }}>
                          <Box sx={{
                            width: `${percentage}%`,
                            height: '100%',
                            bgcolor: option.color,
                            borderRadius: 2,
                            transition: 'width 0.3s ease'
                          }} />
                        </Box>
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            </Paper>

            {/* Filter Groups */}
            {filteredCategories.map((group, groupIndex) => {
              const activeFiltersCount = group.items.reduce((count, item) => 
                count + (filters[item.fieldName]?.length || 0), 0
              );

              // Get items with available options
              const itemsWithOptions = group.items.filter(item => {
                // Always include checkbox fields
                if (item.type === 'checkbox') return true;
                const options = createFilterOptions(item.fieldName, filters);
                return options && Array.isArray(options) && options.length > 0;
              });

              // Skip rendering this group if it has no items with options
              if (itemsWithOptions.length === 0) return null;

              return (
                <Box key={group.title} sx={{ mb: 4, '&:last-child': { mb: 0 } }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1.5,
                    mb: 3
                  }}>
                    <Box sx={{
                      width: 28,
                      height: 28,
                      borderRadius: 1.5,
                      bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
                      color: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <FilterListIcon sx={{ fontSize: 16 }} />
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontFamily: 'StrawberrySansDisplay',
                        fontWeight: 500,
                        color: 'text.primary',
                        fontSize: '1.1rem'
                      }}
                    >
                      {group.title}
                    </Typography>
                    {activeFiltersCount > 0 && (
                      <Chip
                        label={activeFiltersCount}
                        size="small"
                        color="primary"
                        sx={{ 
                          height: 20,
                          '& .MuiChip-label': { px: 1, fontSize: '0.75rem' }
                        }}
                      />
                    )}
                    {activeFiltersCount > 0 && (
                      <Button
                        size="small"
                        onClick={() => {
                            // Get the fields in this group that have active filters
                            const fieldsToReset = group.items
                              .filter(item => filters[item.fieldName] && filters[item.fieldName].length > 0)
                              .map(item => item.fieldName);
                              
                            // Clear these fields by calling onFilterChange with empty arrays
                            fieldsToReset.forEach(field => {
                              onFilterChange(field, []);
                            });
                        }}
                        sx={{ 
                          color: 'text.secondary',
                          '&:hover': { 
                            color: 'error.main'
                          }
                        }}
                      >
                        Clear group
                      </Button>
                    )}
                  </Box>

                  <Grid container spacing={2}>
                    {itemsWithOptions.map(item => {
                      // Check if this is a checkbox field
                      const isCheckboxField = item.type === 'checkbox';
                      
                      // Get filter options based on the filtered hotels by status if status filter is active
                      const options = createFilterOptions(item.fieldName, filters);
                      
                      // Skip if no options are available for this item (unless it's a checkbox)
                      if (!isCheckboxField && (!options || options.length === 0)) return null;

                      // For checkbox fields, show category name with count like other filters
                      if (isCheckboxField) {
                        const isSelected = filters[item.fieldName]?.includes('true') || filters[item.fieldName]?.includes(true);
                        // Get count from options, or calculate directly if options are empty
                        let checkedCount = 0;
                        if (options && options.length > 0 && options[0]) {
                          checkedCount = options[0].count || 0;
                        } else {
                          // Fallback: count from filteredHotels (which respects current filters)
                          checkedCount = filteredHotels.filter(hotel => {
                            const categoryKey = `category_${item.fieldName.toLowerCase()}`;
                            const value = hotel.categoryData?.[categoryKey]?.value;
                            if (value === true) return true;
                            if (value === false || value === null || value === undefined || value === '') return false;
                            const strValue = String(value).toLowerCase().trim();
                            return strValue === 'true' || strValue === 'yes' || strValue === '1' || strValue === 'checked';
                          }).length;
                        }
                        
                        return (
                          <Grid item xs={12} sm={6} key={item.id}>
                            <Paper
                              elevation={0}
                              sx={{
                                p: 3,
                                borderRadius: 3,
                                bgcolor: 'background.paper',
                                boxShadow: theme => `0 2px 8px ${alpha(theme.palette.common.black, 0.04)}`,
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  transform: 'translateY(-2px)',
                                  boxShadow: theme => `0 8px 24px ${alpha(theme.palette.common.black, 0.12)}`
                                }
                              }}
                            >
                              <Typography 
                                variant="subtitle1" 
                                sx={{ 
                                  mb: 2,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  fontWeight: 500,
                                  fontFamily: 'StrawberrySansDisplay',
                                  color: 'text.primary',
                                  fontSize: '1rem'
                                }}
                              >
                                {item.title}
                                {isSelected && (
                                  <Chip
                                    label="1"
                                    size="small"
                                    color="primary"
                                    sx={{ 
                                      height: 20,
                                      '& .MuiChip-label': { px: 1, fontSize: '0.75rem' }
                                    }}
                                  />
                                )}
                              </Typography>
                              
                              <Box sx={{ 
                                display: 'flex', 
                                flexWrap: 'wrap', 
                                gap: 1,
                                maxHeight: 120,
                                overflowY: 'auto',
                                ...commonStyles.scrollbar
                              }}>
                                <Chip
                                  label={`${item.title} (${checkedCount})`}
                                  onClick={() => {
                                    const checked = !isSelected;
                                    onFilterChange(item.fieldName, checked ? ['true'] : []);
                                  }}
                                  color={isSelected ? "primary" : "default"}
                                  variant={isSelected ? "filled" : "outlined"}
                                  size="small"
                                  sx={{
                                    height: 28,
                                    borderRadius: 1.5,
                                    fontFamily: 'StrawberrySansText',
                                    cursor: 'pointer',
                                    '& .MuiChip-label': {
                                      px: 1,
                                      fontSize: '0.75rem',
                                      fontWeight: 500
                                    },
                                    ...(isSelected ? {
                                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                      '&:hover': {
                                        bgcolor: 'error.main',
                                        borderColor: 'error.main'
                                      }
                                    } : {
                                      '&:hover': {
                                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                                        borderColor: 'primary.main'
                                      }
                                    })
                                  }}
                                />
                              </Box>
                            </Paper>
                          </Grid>
                        );
                      }

                      // Regular field rendering with chips
                      return (
                        <Grid item xs={12} sm={6} key={item.id}>
                          <Paper
                            elevation={0}
                            sx={{
                              p: 3,
                              borderRadius: 3,
                              bgcolor: 'background.paper',
                              boxShadow: theme => `0 2px 8px ${alpha(theme.palette.common.black, 0.04)}`,
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: theme => `0 8px 24px ${alpha(theme.palette.common.black, 0.12)}`
                              }
                            }}
                          >
                            <Typography 
                              variant="subtitle1" 
                              sx={{ 
                                mb: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                fontWeight: 500,
                                fontFamily: 'StrawberrySansDisplay',
                                color: 'text.primary',
                                fontSize: '1rem'
                              }}
                            >
                              {item.title}
                              {filters[item.fieldName]?.length > 0 && (
                                <Chip
                                  label={filters[item.fieldName].length}
                                  size="small"
                                  color="primary"
                                  sx={{ 
                                    height: 20,
                                    '& .MuiChip-label': { px: 1, fontSize: '0.75rem' }
                                  }}
                                />
                              )}
                            </Typography>
                            
                            <Box sx={{ 
                              display: 'flex', 
                              flexWrap: 'wrap', 
                              gap: 1,
                              maxHeight: 120,
                              overflowY: 'auto',
                              ...commonStyles.scrollbar
                            }}>
                                {Array.isArray(options) ? options.map(option => {
                                const isSelected = filters[item.fieldName]?.includes(option.value);
                                return (
                                  <Chip
                                    key={option.value}
                                    label={`${option.label} (${option.count})`}
                                    onClick={() => {
                                      const currentValues = filters[item.fieldName] || [];
                                      const newValues = isSelected
                                        ? currentValues.filter(v => v !== option.value)
                                        : [...currentValues, option.value];
                                      onFilterChange(item.fieldName, newValues);
                                    }}
                                    color={isSelected ? "primary" : "default"}
                                    variant={isSelected ? "filled" : "outlined"}
                                    size="small"
                                    sx={{
                                      height: 28,
                                      borderRadius: 1.5,
                                      fontFamily: 'StrawberrySansText',
                                      cursor: 'pointer',
                                      '& .MuiChip-label': {
                                        px: 1,
                                        fontSize: '0.75rem',
                                        fontWeight: 500
                                      },
                                      ...(isSelected ? {
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                        '&:hover': {
                                          bgcolor: 'error.main',
                                          borderColor: 'error.main'
                                        }
                                      } : {
                                        opacity: option.count === 0 ? 0.5 : 1,
                                        '&:hover': {
                                          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                                          borderColor: 'primary.main'
                                        }
                                      })
                                    }}
                                    disabled={!isSelected && option.count === 0}
                                  />
                                );
                                }) : null}
                            </Box>
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              );
            })}

            {/* No Results Message */}
            {filteredCategories.length > 0 && 
             filteredCategories.every(group => {
               const itemsWithOptions = group.items.filter(item => {
                 const options = createFilterOptions(item.fieldName, filters);
                   return options && Array.isArray(options) && options.length > 0;
               });
               return itemsWithOptions.length === 0;
             }) && (
              <Box 
                sx={{ 
                  p: 4, 
                  textAlign: 'center', 
                  color: 'text.secondary',
                  bgcolor: (theme) => alpha(theme.palette.background.paper, 0.5),
                  borderRadius: 3,
                  border: '1px dashed',
                  borderColor: 'divider',
                  animation: 'fadeIn 0.5s ease-out',
                  '@keyframes fadeIn': {
                    '0%': {
                      opacity: 0,
                      transform: 'translateY(10px)'
                    },
                    '100%': {
                      opacity: 1,
                      transform: 'translateY(0)'
                    }
                  }
                }}
              >
                <FilterListIcon sx={{ fontSize: 48, opacity: 0.5, mb: 1, color: 'primary.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 500, mb: 1 }}>No matching filter options</Typography>
                <Typography variant="body2" sx={{ mt: 1, opacity: 0.7, maxWidth: 400, mx: 'auto' }}>
                  Your current filter selection has filtered out all other options. Try removing some filters to see more options.
                </Typography>
                <Button 
                  variant="outlined" 
                  size="small" 
                    onClick={() => {
                      // Get current filter fields
                      const filterFields = Object.keys(filters);
                      
                      // Clear all filters by calling onFilterChange with empty arrays for each field
                      filterFields.forEach(field => {
                        onFilterChange(field, []);
                      });
                      
                      // Close dialog
                      onClose();
                    }}
                  sx={{ 
                    mt: 2,
                    borderRadius: 6,
                    px: 3,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                    Reset
                </Button>
              </Box>
            )}

            {/* Empty State */}
            {filteredCategories.length === 0 && (
              <Box 
                sx={{ 
                  p: 4, 
                  textAlign: 'center', 
                  color: 'text.secondary',
                  bgcolor: (theme) => alpha(theme.palette.background.paper, 0.5),
                  borderRadius: 3,
                  border: '1px dashed',
                  borderColor: 'divider',
                  animation: 'fadeIn 0.5s ease-out',
                  '@keyframes fadeIn': {
                    '0%': {
                      opacity: 0,
                      transform: 'translateY(10px)'
                    },
                    '100%': {
                      opacity: 1,
                      transform: 'translateY(0)'
                    }
                  }
                }}
              >
                <FilterListIcon sx={{ fontSize: 48, opacity: 0.5, mb: 1, color: 'primary.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 500, mb: 1 }}>No matching filters found</Typography>
                <Typography variant="body2" sx={{ mt: 1, opacity: 0.7, maxWidth: 400, mx: 'auto' }}>
                  Try a different search term or check your spelling
                </Typography>
                <Button 
                  variant="outlined" 
                  size="small" 
                  onClick={() => {
                    // Get current filter fields
                    const filterFields = Object.keys(filters);
                    
                    // Clear all filters by calling onFilterChange with empty arrays for each field
                    filterFields.forEach(field => {
                      onFilterChange(field, []);
                    });
                    
                    // Close dialog
                    onClose();
                  }}
                  sx={{ 
                    mt: 2,
                    borderRadius: 6,
                    px: 3,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  Reset
                </Button>
              </Box>
            )}
          </Box>
          )}
          
          {/* Export Tab */}
          {dialogTab === 1 && (
            <ExportContent
              categories={categories}
              filters={filters}
              filteredHotels={filteredHotels}
              hotels={hotels}
              onExport={handleExport}
              onCancel={onClose}
              isExporting={isExporting}
            />
          )}
        </DialogContent>

        <DialogActions sx={{ 
          p: { xs: 3, sm: 4 },
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          position: 'relative',
          zIndex: 1,
          gap: 2,
        }}>
          <Button
            onClick={() => {
              // Get current filter fields
              const filterFields = Object.keys(filters);
              
              // Clear all filters by calling onFilterChange with empty arrays for each field
              filterFields.forEach(field => {
                onFilterChange(field, []);
              });
              
              // Close dialog
              onClose();
            }}
            color="inherit"
            disabled={Object.keys(filters).length === 0}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1.5,
              fontWeight: 500,
              fontFamily: 'StrawberrySansText',
              textTransform: 'none',
              opacity: Object.keys(filters).length === 0 ? 0.5 : 1,
              '&:hover': {
                bgcolor: theme => alpha(theme.palette.error.main, 0.08),
                color: 'error.main',
                transform: 'translateY(-1px)',
                boxShadow: theme => `0 4px 12px ${alpha(theme.palette.error.main, 0.2)}`
              },
              transition: 'all 0.2s ease-in-out'
            }}
          >
            Reset
          </Button>
          <Button 
            onClick={onClose}
            variant="contained"
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1.5,
              fontWeight: 500,
              fontFamily: 'StrawberrySansText',
              textTransform: 'none',
              boxShadow: theme => `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: theme => `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`
              },
              transition: 'all 0.2s ease-in-out'
            }}
          >
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>

      {/* Save Filter Combination Dialog */}
      <Dialog
        open={saveDialogOpen}
        onClose={() => setSaveDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1,
            bgcolor: 'background.paper',
            backgroundImage: 'none',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: theme => `
              0 24px 48px ${alpha(theme.palette.common.black, 0.15)},
              0 8px 16px ${alpha(theme.palette.common.black, 0.1)}
            `,
            // Enhanced glassmorphism effect
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: theme => theme.palette.mode === 'dark' 
                ? 'linear-gradient(135deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0.05) 100%)'
                : 'linear-gradient(135deg, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0.4) 100%)',
              pointerEvents: 'none',
              zIndex: -1
            }
          }
        }}
        TransitionComponent={Fade}
        transitionDuration={200}
      >
        <DialogTitle sx={{ 
          p: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: theme => `radial-gradient(circle at top right, ${alpha(theme.palette.primary.main, 0.08)}, transparent 70%)`,
            pointerEvents: 'none',
          }
        }}>
          <Avatar sx={{ 
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
            color: 'primary.main',
            width: 40,
            height: 40
          }}>
            <SaveIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ 
              fontWeight: 400,
              fontFamily: 'StrawberrySansDisplay',
              letterSpacing: '-0.02em'
            }}>
              Save Filter Combination
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ 
                opacity: 0.8,
                fontFamily: 'StrawberrySansText'
              }}
            >
              Save your current filter selection for quick access
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 2.5, pt: 2 }}>
          <TextField
            autoFocus
            margin="dense"
            label="Combination Name"
            fullWidth
            variant="outlined"
            value={newComboName}
            onChange={(e) => setNewComboName(e.target.value)}
            placeholder="e.g., Swedish Hotels"
            InputProps={{
              sx: {
                borderRadius: 1.5,
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: (theme) => alpha(theme.palette.primary.main, 0.3)
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  boxShadow: '0 0 0 3px rgba(0,123,255,0.1)'
                }
              }
            }}
          />
          <Box sx={{ 
            mt: 2, 
            p: 1.5, 
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05),
            borderRadius: 2,
            border: '1px solid',
            borderColor: (theme) => alpha(theme.palette.primary.main, 0.1)
          }}>
            <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <FilterListIcon sx={{ fontSize: 14 }} />
              {Object.values(filters).reduce((sum, values) => sum + values.length, 0)} active filters will be saved
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 1 }}>
          <Button 
            onClick={() => setSaveDialogOpen(false)} 
            color="inherit"
            disabled={isSaving}
            sx={{
              borderRadius: 6,
              px: 2.5,
              '&:hover': {
                bgcolor: (theme) => alpha(theme.palette.common.black, 0.05)
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={saveFilterCombo} 
            variant="contained"
            disabled={!newComboName.trim() || Object.keys(filters).length === 0 || isSaving}
            sx={{
              borderRadius: 6,
              px: 2.5,
              minWidth: 100,
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              '&:hover': {
                boxShadow: '0 6px 16px rgba(0,0,0,0.15)'
              }
            }}
          >
            {isSaving ? (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1 
              }}>
                <Box 
                  sx={{ 
                    width: 16, 
                    height: 16, 
                    borderRadius: '50%',
                    border: '2px solid',
                    borderColor: 'rgba(255,255,255,0.3)',
                    borderTopColor: '#fff',
                    animation: 'spin 1s linear infinite',
                  }}
                />
                Saving...
              </Box>
            ) : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Filter Combo Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleComboMenuClose}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: (theme) => `0 4px 20px ${alpha(theme.palette.common.black, 0.15)}`,
            minWidth: 180,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider'
          }
        }}
        TransitionComponent={Fade}
        transitionDuration={150}
      >
        <MenuItem 
          onClick={() => loadFilterCombo(selectedCombo)}
          sx={{
            py: 1.5,
            '&:hover': {
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
              '& .MuiListItemIcon-root': {
                color: 'primary.main'
              }
            }
          }}
        >
          <ListItemIcon sx={{ color: 'text.secondary' }}>
            <FilterListIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Apply Filters" />
        </MenuItem>
        <Divider />
        <MenuItem 
          onClick={handleDeleteCombo}
          sx={{
            padding: '12px',
            color: 'error.main',
            '&:hover': {
              backgroundColor: 'rgba(255, 82, 82, 0.05)'
            }
          }}
        >
          <ListItemIcon sx={{ color: 'error.main' }}>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Delete" sx={{ color: 'error.main' }} />
        </MenuItem>
      </Menu>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ 
            width: '100%',
            alignItems: 'center',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            '& .MuiAlert-icon': {
              fontSize: '1.25rem'
            }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AdvancedFiltersDialog; 